import React, { useState, useCallback, useEffect } from "react";
import { RiseUserAction } from "@/types/RiseUserAction";
import { ActionGroupBadge } from "../ActionGroupBadge";
import { ActionProgressBar } from "./ActionProgressBar";
import { ConfirmationDialog } from "@/components/RiseAction/ConfirmationDialog";
import { useMutation, ApolloError } from "@apollo/client";
import { toast } from "sonner";
import { Spinner } from "@/assets/svg/Spinner";
import { useActionStudents } from "../RiseActionDetails/useActionStudents";
import {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { ChartActionsButton } from "@/components/ChartActionsButton";
import { ChartOptionsMenuItem } from "@/components/ChartOptionsMenuItem";
import { useTranslation } from "react-i18next";
import { actionIsComplete, actionIsStarted } from "./util";
import { UPDATE_ACTION_STATUS } from "@/api/mutations/updateActionStatus";
import { USER_ACTIONS } from "@/api/queries/userActions";
import { cn } from "@/lib/utils";
import {
  UpdateActionStatusMutation,
  UpdateActionStatusMutationVariables,
  RiseUserActionStudentStatusEnum,
  SortDirectionEnum,
  StudentListRiseActionDetailsSortableFieldEnum,
} from "@/__generated__/types";
import { CheckMarkCircle } from "./CheckMarkCircle";
import { FacePile } from "./FacePile";
import { StudentOrdering } from "@/types/StudentOrdering";

export const RiseAction = ({
  action,
  onClick,
}: {
  action: RiseUserAction;
  onClick: (action: RiseUserAction) => void;
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [updateError, setUpdateError] = useState<ApolloError | null>(null);
  const { t } = useTranslation("translation", {
    keyPrefix: "strategyBoard.actions",
  });

  const handleMouseEnter = () => setIsHovering(true);
  const handleMouseLeave = () => setIsHovering(false);

  const { items, loading } = useActionStudents({
    paginationVariables: {
      first: 4,
    },
    ordering: new StudentOrdering(
      StudentListRiseActionDetailsSortableFieldEnum.Status,
      SortDirectionEnum.Desc,
      "rise.actionDetails",
    ),
    actionId: action.id,
  });

  const isComplete = actionIsComplete(action);

  const menuOptions: {
    label: string;
    onClick?: (e?: React.MouseEvent) => void;
  }[] = [
    // TODO: re-enable when feedback link is ready
    // {
    //   label: t("feedback"),
    //   onClick: (e) => {
    //     e?.stopPropagation();
    //     window.location.href = constants["FEEDBACK_FORM_URL"];
    //   },
    // },
  ];

  const [
    markAllComplete,
    { loading: markCompleteLoading, error: markCompleteError },
  ] = useMutation<
    UpdateActionStatusMutation,
    UpdateActionStatusMutationVariables
  >(UPDATE_ACTION_STATUS, {
    refetchQueries: [USER_ACTIONS],
    awaitRefetchQueries: true,
  });

  const disabled = markCompleteLoading;

  useEffect(() => {
    if (markCompleteError && markCompleteError !== updateError) {
      toast.error(t("errors.markComplete.title"), {
        description: t("errors.markComplete.description"),
      });
      setUpdateError(markCompleteError);
    }
  }, [markCompleteError, updateError, t]);

  const updateActionToComplete = useCallback(
    (action: RiseUserAction) => {
      markAllComplete({
        variables: {
          riseActionId: action.id,
          status: RiseUserActionStudentStatusEnum.Done,
        },
      });
    },
    [markAllComplete],
  );

  if (!isComplete) {
    menuOptions.unshift({
      label: actionIsStarted(action)
        ? t("markRemainingDone")
        : t("markAllDone"),
      onClick: (e) => {
        e?.stopPropagation();
        setDialogOpen(true);
      },
    });
  }

  return (
    <>
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => {
          onClick(action);
        }}
        className={cn(
          "cursor-pointer rounded-xl border p-6 pl-[19px]",
          disabled && "cursor-default opacity-50",
        )}
      >
        <div className="relative flex gap-6">
          {markCompleteLoading && (
            <div className="absolute flex h-full w-full items-center justify-center">
              <Spinner className="text-primary" />
            </div>
          )}
          <div className="flex items-center pr-[26px]">
            <CheckMarkCircle
              complete={isComplete}
              onClick={() => setDialogOpen(true)}
              disabled={disabled}
            />
          </div>
          <div className={isComplete ? "opacity-50" : ""}>
            <div className="mb-2 flex gap-2">
              <div className="text-base font-semibold">
                {action.actionType?.displayName}
              </div>
              {action.actionGroup && (
                <ActionGroupBadge actionGroup={action.actionGroup} />
              )}
            </div>
            <div className="mb-3 font-semibold text-muted-foreground">
              {action.actionType?.description}
            </div>
            <div className="mb-1.5">
              <ActionProgressBar
                showDetails={disabled || isHovering || isComplete}
                action={action}
              />
            </div>
          </div>
          <div className="flex flex-grow items-center justify-end">
            <FacePile students={items} loading={loading} />
          </div>
          <div className="flex items-center justify-end">
            {menuOptions.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  asChild
                  data-testid="engagement-dropdown-menu-trigger"
                >
                  <ChartActionsButton disabled={disabled} />
                </DropdownMenuTrigger>
                <DropdownMenuPortal>
                  <ChartOptionsMenuItem {...{ menuOptions }} />
                </DropdownMenuPortal>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
      <ConfirmationDialog
        open={dialogOpen}
        action={action}
        onOpenChange={setDialogOpen}
        onConfirm={updateActionToComplete}
      />
    </>
  );
};
